/**
 * Unified e-commerce analytics helper for GA4 + Yandex.Metrica.
 *
 * Wraps `window.gtag(...)` (GA4) and `window.ym(id, 'reachGoal/hit', ...)`
 * (Yandex.Metrica) behind a single typed API. Both are no-ops when the
 * underlying counters haven't loaded (e.g. preview/local dev without env
 * vars, ad blockers, CSP failures), so calling sites never have to
 * defensively check for the global.
 *
 * Event taxonomy follows GA4's standard e-commerce schema
 * (https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase)
 * because Google Ads Performance Max + DV360 require GA4-standard events.
 * Yandex.Metrica events use its dataLayer `ecommerce` push convention
 * (https://yandex.com/support/metrica/data/e-commerce.html).
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const YM_ID = process.env.REACT_APP_YANDEX_METRICA_ID;

// ---- GA4 ---------------------------------------------------------------
const gtagSafe = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function' && GA_ID) {
      window.gtag(...args);
    }
  } catch (_) { /* no-op */ }
};

// ---- Yandex.Metrica ---------------------------------------------------
// YM's e-commerce protocol uses a dataLayer push (configured via
// `ecommerce: 'dataLayer'` in YandexMetrica.js init). The shape mirrors
// Google's Universal Analytics enhanced-ecommerce schema, NOT GA4 events.
const ymDataLayerPush = (ecommerceBlock, currencyCode = 'EUR') => {
  try {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        ecommerce: {
          currencyCode,
          ...ecommerceBlock,
        },
      });
    }
  } catch (_) { /* no-op */ }
};

// reachGoal, Yandex's high-level conversion event (used in retargeting +
// goal-completion reports). Fires alongside the dataLayer push so both
// e-commerce funnel views AND goal-completion reports light up.
const ymReachGoal = (goalName, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.ym === 'function' && YM_ID) {
      window.ym(Number(YM_ID), 'reachGoal', goalName, params);
    }
  } catch (_) { /* no-op */ }
};

// ---- Public API -------------------------------------------------------

/**
 * Format a product (or cart-item) into the GA4 `items[]` shape.
 * Accepts either a raw product object (from /api/products/{id}) or a
 * cart item (which carries quantity + selectedDosage).
 */
const toGA4Item = (productOrCartItem, currentLanguage = 'en') => {
  const p = productOrCartItem;
  const name = (p.name && typeof p.name === 'object')
    ? (p.name[currentLanguage] || p.name.en || '')
    : (p.name || '');
  const category = p.category_id || p.category || 'research-peptide';
  const variant = p.selectedDosage || p.dosage || (p.selected_variant && p.selected_variant.dosage) || '';
  return {
    item_id: p.id,
    item_name: String(name).slice(0, 100),
    item_brand: 'Penlab Peptide',
    item_category: category,
    item_variant: variant,
    price: Number(p.price || 0),
    quantity: Number(p.quantity || 1),
  };
};

const toYMItem = (productOrCartItem, currentLanguage = 'en') => {
  const i = toGA4Item(productOrCartItem, currentLanguage);
  // YM uses GA-classic naming.
  return {
    id: i.item_id,
    name: i.item_name,
    brand: i.item_brand,
    category: i.item_category,
    variant: i.item_variant,
    price: i.price,
    quantity: i.quantity,
  };
};

/**
 * Fires when a user lands on a /product/{id} detail page. Equivalent of
 * GA4 `view_item` + Yandex `ecommerce.detail` + reachGoal('view_item').
 */
export const trackViewItem = (product, currentLanguage = 'en') => {
  if (!product || !product.id) return;
  const item = toGA4Item({ ...product, quantity: 1 }, currentLanguage);
  gtagSafe('event', 'view_item', {
    currency: 'EUR',
    value: item.price,
    items: [item],
  });
  ymDataLayerPush({ detail: { products: [toYMItem(product, currentLanguage)] } });
  ymReachGoal('view_item', { product_id: product.id });
};

/**
 * Fires when a user adds a product to cart. Equivalent of GA4
 * `add_to_cart` + Yandex `ecommerce.add` + reachGoal('add_to_cart').
 */
export const trackAddToCart = (product, quantity = 1, currentLanguage = 'en') => {
  if (!product || !product.id) return;
  const enriched = { ...product, quantity };
  const item = toGA4Item(enriched, currentLanguage);
  gtagSafe('event', 'add_to_cart', {
    currency: 'EUR',
    value: item.price * quantity,
    items: [item],
  });
  ymDataLayerPush({ add: { products: [toYMItem(enriched, currentLanguage)] } });
  ymReachGoal('add_to_cart', { product_id: product.id, quantity });
};

/**
 * Fires when a user lands on /checkout (after cart has items). Equivalent
 * of GA4 `begin_checkout` + Yandex `ecommerce.purchase.actionField.step:1`.
 */
export const trackBeginCheckout = (cartItems, total, currentLanguage = 'en') => {
  if (!cartItems || cartItems.length === 0) return;
  const items = cartItems.map((c) => toGA4Item(c, currentLanguage));
  gtagSafe('event', 'begin_checkout', {
    currency: 'EUR',
    value: Number(total || 0),
    items,
  });
  ymDataLayerPush({
    checkout: {
      actionField: { step: 1 },
      products: cartItems.map((c) => toYMItem(c, currentLanguage)),
    },
  });
  ymReachGoal('begin_checkout', { value: Number(total || 0), item_count: items.length });
};

/**
 * Fires on /checkout/success once the Stripe payment has been verified
 * and the order has loaded. Equivalent of GA4 `purchase` (the most
 * important event in the funnel, feeds Google Ads conversions, DV360,
 * Performance Max optimisation) + Yandex `ecommerce.purchase`.
 *
 * `order` shape (from /api/orders/{id}):
 *   { id, total, subtotal, shipping_cost, tax, items: [{product_id,
 *     product_name, price, quantity, dosage}, ...], coupon_code }
 */
export const trackPurchase = (order, currentLanguage = 'en') => {
  if (!order || !order.id) return;
  const rawItems = order.items || [];
  const items = rawItems.map((it) => ({
    item_id: it.product_id || it.id,
    item_name: typeof it.product_name === 'object'
      ? (it.product_name[currentLanguage] || it.product_name.en || '')
      : (it.product_name || it.name || ''),
    item_brand: 'Penlab Peptide',
    item_category: it.category_id || 'research-peptide',
    item_variant: it.dosage || it.selectedDosage || '',
    price: Number(it.price || 0),
    quantity: Number(it.quantity || 1),
  }));
  const ymItems = rawItems.map((it) => ({
    id: it.product_id || it.id,
    name: typeof it.product_name === 'object'
      ? (it.product_name[currentLanguage] || it.product_name.en || '')
      : (it.product_name || it.name || ''),
    brand: 'Penlab Peptide',
    price: Number(it.price || 0),
    quantity: Number(it.quantity || 1),
    variant: it.dosage || '',
  }));
  gtagSafe('event', 'purchase', {
    transaction_id: order.id,
    currency: order.currency || 'EUR',
    value: Number(order.total || 0),
    tax: Number(order.tax || 0),
    shipping: Number(order.shipping_cost || 0),
    coupon: order.coupon_code || undefined,
    items,
  });
  ymDataLayerPush({
    purchase: {
      actionField: {
        id: order.id,
        revenue: Number(order.total || 0),
        tax: Number(order.tax || 0),
        shipping: Number(order.shipping_cost || 0),
        coupon: order.coupon_code || undefined,
      },
      products: ymItems,
    },
  }, order.currency || 'EUR');
  ymReachGoal('purchase', {
    order_id: order.id,
    revenue: Number(order.total || 0),
  });
};

/**
 * Fires when a user performs a search via ?search=X on the products page.
 * Adds a `view_search_results` GA4 event that Google Ads' smart bidding
 * uses to model purchase intent. Also helps explain Soft-404 traffic
 * patterns to GSC (no-results searches → noindex CTA shown).
 */
export const trackSearch = (searchTerm, resultCount) => {
  if (!searchTerm) return;
  gtagSafe('event', 'view_search_results', {
    search_term: String(searchTerm).slice(0, 80),
    result_count: Number(resultCount || 0),
  });
  ymReachGoal('view_search_results', { q: searchTerm, count: resultCount });
};

// Module-level guard against double-firing the same purchase event in
// React StrictMode + dev double-effects. Caller is expected to check the
// returned flag before invoking trackPurchase a second time.
const _purchasedOrders = new Set();
export const isPurchaseAlreadyTracked = (orderId) => _purchasedOrders.has(orderId);
export const markPurchaseTracked = (orderId) => _purchasedOrders.add(orderId);
